/**
 * StateMachine - Generic state machine implementation
 * Used for Player states (Idle, Rolling, Jumping, Falling, Dead)
 */

export default class StateMachine {
    constructor(owner, initialState = null) {
        this.owner = owner;
        this.states = new Map();
        this.currentState = null;
        this.previousState = null;
        this.stateTime = 0;
    }

    /**
     * Add a state to the machine
     * @param {string} name - State name
     * @param {BaseState} state - State instance
     */
    addState(name, state) {
        state.stateMachine = this;
        state.owner = this.owner;
        this.states.set(name, state);
    }

    /**
     * Set the initial state
     * @param {string} name - State name
     */
    start(name) {
        if (!this.states.has(name)) {
            console.error(`StateMachine: State '${name}' not found`);
            return;
        }
        
        this.currentState = this.states.get(name);
        this.currentState.enter();
        this.stateTime = 0;
    }

    /**
     * Transition to a new state
     * @param {string} name - State name
     * @param {Object} data - Optional data to pass to new state
     */
    setState(name, data = null) {
        if (!this.states.has(name)) {
            console.error(`StateMachine: State '${name}' not found`);
            return;
        }

        const newState = this.states.get(name);
        
        // Don't transition to same state unless forced
        if (this.currentState === newState && !data?.force) {
            return;
        }

        // Exit current state
        if (this.currentState) {
            this.currentState.exit();
            this.previousState = this.currentState;
        }

        // Enter new state
        this.currentState = newState;
        this.stateTime = 0;
        this.currentState.enter(data);
    }

    /**
     * Update current state
     * @param {number} delta - Delta time
     */
    update(delta) {
        if (this.currentState) {
            this.stateTime += delta;
            this.currentState.update(delta);
        }
    }

    /**
     * Get current state name
     */
    getCurrentStateName() {
        for (const [name, state] of this.states) {
            if (state === this.currentState) {
                return name;
            }
        }
        return null;
    }

    /**
     * Check if in a specific state
     * @param {string} name - State name to check
     */
    isState(name) {
        return this.states.get(name) === this.currentState;
    }

    /**
     * Get how long we've been in current state
     */
    getStateTime() {
        return this.stateTime;
    }

    /**
     * Destroy all states
     */
    destroy() {
        this.states.forEach(state => {
            if (state.destroy) {
                state.destroy();
            }
        });
        this.states.clear();
        this.currentState = null;
        this.previousState = null;
    }
}
